/* ==========================================================================
   PADENUS - CART DRAWER & RESERVATION COUNTDOWN CONTROLLER
   ========================================================================== */

class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-padenus-cart-drawer]');
    this.overlay = document.getElementById('CartDrawerOverlay');
    this.closeButtons = document.querySelectorAll('[data-cart-drawer-close]');
    this.openButtons = document.querySelectorAll('[data-cart-drawer-trigger]');
    this.countdownEl = document.getElementById('CartTimerDisplay');
    this.itemsContainer = document.getElementById('CartDrawerItems');
    this.subtotalEl = document.getElementById('CartDrawerSubtotal');
    this.shippingFill = document.getElementById('FreeShippingFill');
    this.shippingMsg = document.getElementById('FreeShippingMsg');
    this.freeShippingThreshold = Number(this.drawer?.dataset.shippingThreshold || 50);
    this.countdownSeconds = Number(this.drawer?.dataset.countdownSeconds || 300);
    this.shippingSuccess = this.drawer?.dataset.shippingSuccess || '';
    this.shippingRemaining = this.drawer?.dataset.shippingRemaining || '';
    this.emptyIcon = this.drawer?.dataset.emptyIcon || '';
    this.emptyTitle = this.drawer?.dataset.emptyTitle || '';
    this.emptyDescription = this.drawer?.dataset.emptyDescription || '';
    this.emptyLinkLabel = this.drawer?.dataset.emptyLinkLabel || '';
    this.emptyLinkUrl = this.drawer?.dataset.emptyLinkUrl || '/collections/all';
    this.removeLabel = this.drawer?.dataset.removeLabel || '';
    this.quantityLabel = this.drawer?.dataset.quantityLabel || 'Quantity';
    this.decreaseLabel = this.drawer?.dataset.decreaseLabel || 'Decrease quantity for [product]';
    this.increaseLabel = this.drawer?.dataset.increaseLabel || 'Increase quantity for [product]';

    this.init();
  }

  init() {
    this.bindEvents();
    this.startCountdown();
    this.refreshCart();
  }

  bindEvents() {
    this.openButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Intercept Quick Add buttons
    document.addEventListener('click', (e) => {
      const atcBtn = e.target.closest('[data-quick-add]');
      if (atcBtn) {
        e.preventDefault();
        const variantId = atcBtn.dataset.variantId || '1';
        this.addItem({ id: variantId, quantity: 1 });
      }
    });
  }

  open() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  isOpen() {
    return this.drawer && this.drawer.classList.contains('is-open');
  }

  startCountdown() {
    if (!this.countdownEl) return;
    let remaining = parseInt(sessionStorage.getItem('padenus_cart_timer')) || this.countdownSeconds;

    setInterval(() => {
      if (remaining <= 0) {
        remaining = this.countdownSeconds;
      }
      remaining--;
      sessionStorage.setItem('padenus_cart_timer', remaining);

      const mins = String(Math.floor(remaining / 60)).padStart(2, '0');
      const secs = String(remaining % 60).padStart(2, '0');
      this.countdownEl.textContent = `${mins}:${secs}`;
    }, 1000);
  }

  async addItem(item) {
    try {
      const res = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, quantity: item.quantity || 1 })
      });
      if (res.ok) {
        await this.refreshCart();
        this.open();
        return;
      }
    } catch (err) {
      console.error('Shopify ajax add error', err);
    }
  }

  async changeQuantity(lineIndex, newQty) {
    try {
      await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: lineIndex, quantity: newQty })
      });
      await this.refreshCart();
    } catch (e) {
      console.error(e);
    }
  }

  async refreshCart() {
    try {
      const res = await fetch(`${window.routes.cart_url}.js`);
      if (!res.ok) return;
      const cart = await res.json();
      this.renderCart(cart);
    } catch (e) {
      console.error(e);
    }
  }

  renderCart(cart) {
    // Update badge counts
    const badges = document.querySelectorAll('.cart-count-badge');
    badges.forEach(b => {
      b.textContent = cart.item_count;
    });

    // Update Subtotal
    const subtotalDollars = cart.total_price / 100;
    if (this.subtotalEl) {
      this.subtotalEl.textContent = this.formatMoney(cart.total_price, cart.currency);
    }

    // Update Free Shipping Bar
    if (this.shippingFill && this.shippingMsg) {
      const pct = Math.min(100, (subtotalDollars / this.freeShippingThreshold) * 100);
      this.shippingFill.style.width = `${pct}%`;
      if (subtotalDollars >= this.freeShippingThreshold) {
        this.shippingMsg.innerHTML = this.shippingSuccess;
      } else {
        const diff = Math.max(0, Math.round((this.freeShippingThreshold - subtotalDollars) * 100));
        this.shippingMsg.innerHTML = this.shippingRemaining.replace('[amount]', this.formatMoney(diff, cart.currency));
      }
    }

    if (!this.itemsContainer) return;

    if (cart.items.length === 0) {
      this.itemsContainer.innerHTML = `
        <div style="text-align: center; padding: 3rem 1rem; color: #7C746B;">
          ${this.emptyIcon ? `<div style="font-size: 2.5rem; margin-bottom: 0.5rem;">${this.emptyIcon}</div>` : ''}
          <p style="font-size: 1rem; font-weight: 600; color: #2B2621; margin-bottom: 0.5rem;">${this.emptyTitle}</p>
          <p style="font-size: 0.85rem; margin-bottom: 1.5rem;">${this.emptyDescription}</p>
          <a href="${this.emptyLinkUrl}" class="btn btn-primary" style="display: inline-block; padding: 0.6rem 1.5rem;">${this.emptyLinkLabel}</a>
        </div>
      `;
      return;
    }

    let html = '';
    cart.items.forEach((item, idx) => {
      const itemPrice = this.formatMoney(item.final_line_price, cart.currency);
      const lineNum = idx + 1;
      const productTitle = item.product_title || item.title || '';
      const variantImage = item.featured_image?.url || item.image || '';
      const variantOptions = (item.options_with_values || [])
        .map(option => `<span>${this.escapeHtml(option.name)}: ${this.escapeHtml(option.value)}</span>`)
        .join('<span aria-hidden="true"> • </span>');
      const decreaseLabel = this.decreaseLabel.replace('[product]', productTitle);
      const increaseLabel = this.increaseLabel.replace('[product]', productTitle);
      html += `
        <div class="cart-item" data-variant-id="${this.escapeAttribute(item.variant_id)}">
          ${variantImage ? `<img src="${this.escapeAttribute(variantImage)}" alt="${this.escapeAttribute(productTitle)}" width="80" height="80" class="cart-item__image">` : ''}
          <div class="cart-item__details">
            <div class="cart-item__header">
              <h3 class="cart-item__title">${this.escapeHtml(productTitle)}</h3>
              <button type="button" class="cart-item__remove" onclick="window.cartDrawer.changeQuantity(${lineNum}, 0)" title="${this.escapeAttribute(this.removeLabel)}" aria-label="${this.escapeAttribute(this.removeLabel)}">&times;</button>
            </div>
            ${variantOptions ? `<p class="cart-item__variant">${variantOptions}</p>` : ''}
            <div class="cart-item__bottom">
              <div class="quantity-stepper">
                <button type="button" class="quantity-btn" onclick="window.cartDrawer.changeQuantity(${lineNum}, ${item.quantity - 1})" aria-label="${this.escapeAttribute(decreaseLabel)}">&minus;</button>
                <input type="text" class="quantity-input" value="${item.quantity}" readonly aria-label="${this.escapeAttribute(`${this.quantityLabel}: ${productTitle}`)}">
                <button type="button" class="quantity-btn" onclick="window.cartDrawer.changeQuantity(${lineNum}, ${item.quantity + 1})" aria-label="${this.escapeAttribute(increaseLabel)}">&plus;</button>
              </div>
              <span class="cart-item__price">${this.escapeHtml(itemPrice)}</span>
            </div>
          </div>
        </div>
      `;
    });
    this.itemsContainer.innerHTML = html;
  }

  escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = String(value ?? '');
    return element.innerHTML;
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/`/g, '&#96;');
  }

  formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (_error) {
      return (cents / 100).toFixed(2);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawerController();
});
